import type { AnyRootRoute, AnyRoute } from '@tanstack/react-router'
import type { NavItem, NavSection } from '@wehunt/ui'
import type {
  BadgeContext,
  BadgeProvider,
  ComposedShell,
  NavItemContribution,
  StreamContribution,
} from './types'

type Section = 'main' | 'account'

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Compose a list of stream contributions into a shell. Fails fast on any
 * mechanical conflict (duplicate stream id, nav item id, route path, or
 * settings section path).
 *
 * Nav item sort: items with `order` come first (ascending), then items
 * without `order`, both in registration (contribution) order for ties.
 */
export function composeStreams(contributions: StreamContribution[]): ComposedShell {
  assertNoDuplicateStreamIds(contributions)

  const mainItems = collectAndSort(contributions, 'main')
  const accountItems = collectAndSort(contributions, 'account')

  assertNoDuplicateNavItemIds(mainItems, 'main')
  assertNoDuplicateNavItemIds(accountItems, 'account')
  assertNoDuplicateSettingsPaths(contributions)

  const badgeProviders: BadgeProvider[] = [
    ...providersFor(mainItems, 'main'),
    ...providersFor(accountItems, 'account'),
  ]

  // Detect duplicate paths eagerly. We invoke each contribution's route
  // builder with a sentinel root so we can walk the resulting tree at
  // compose time — these throwaway routes are not the ones consumed by the
  // app; the `routes` callback below builds fresh ones with the real root.
  const sentinelRoot = {} as unknown as AnyRootRoute
  let probeRoutes: AnyRoute[]
  try {
    probeRoutes = contributions.flatMap((c) => c.routes(sentinelRoot))
  } catch {
    // Some contributions may need a real root to build their tree. If the
    // probe fails, fall back to lazy validation inside `routes()`.
    probeRoutes = []
  }
  if (probeRoutes.length > 0) {
    assertNoDuplicateRoutePaths(probeRoutes)
  }

  const routesFn = (rootRoute: AnyRootRoute): AnyRoute[] => {
    const routes = contributions.flatMap((c) => c.routes(rootRoute))
    assertNoDuplicateRoutePaths(routes)
    return routes
  }

  const navSectionsFn = (badges: BadgeContext): NavSection[] => [
    { items: mainItems.map((item) => toNavItem(item, 'main', badges)) },
    {
      label: 'Account',
      items: accountItems.map((item) => toNavItem(item, 'account', badges)),
    },
  ]

  return {
    routes: routesFn,
    navSections: navSectionsFn,
    badgeProviders,
  }
}

// ---------------------------------------------------------------------------
// Conflict detection
// ---------------------------------------------------------------------------

function assertNoDuplicateStreamIds(contributions: StreamContribution[]): void {
  const seen = new Set<string>()
  for (const c of contributions) {
    if (seen.has(c.id)) {
      throw new Error(
        `composeStreams: duplicate stream id "${c.id}". ` +
          `Each StreamContribution must have a unique id.`,
      )
    }
    seen.add(c.id)
  }
}

function assertNoDuplicateNavItemIds(items: NavItemContribution[], section: Section): void {
  const seen = new Set<string>()
  for (const item of items) {
    if (seen.has(item.id)) {
      throw new Error(
        `composeStreams: duplicate nav item id "${item.id}" in section "${section}". ` +
          `Nav item ids must be unique within a section.`,
      )
    }
    seen.add(item.id)
  }
}

function assertNoDuplicateSettingsPaths(contributions: StreamContribution[]): void {
  const seen = new Set<string>()
  for (const c of contributions) {
    for (const section of c.settingsSections ?? []) {
      if (seen.has(section.path)) {
        throw new Error(
          `composeStreams: duplicate settings section path "${section.path}". ` +
            `Each settings section must have a unique path.`,
        )
      }
      seen.add(section.path)
    }
  }
}

function assertNoDuplicateRoutePaths(routes: AnyRoute[]): void {
  const seen = new Set<string>()
  for (const route of routes) {
    const path = route.fullPath
    if (path === undefined) continue
    if (seen.has(path)) {
      throw new Error(
        `composeStreams: duplicate route path "${path}". ` +
          `Each stream's routes must contribute distinct paths.`,
      )
    }
    seen.add(path)
  }
}

// ---------------------------------------------------------------------------
// Nav item collection / sorting
// ---------------------------------------------------------------------------

/**
 * Collect nav items for a section across all contributions, preserving the
 * registration order. Then stable-sort by `order` ascending — items without
 * an `order` field sort after items that have one, retaining their
 * registration order.
 */
function collectAndSort(
  contributions: StreamContribution[],
  section: Section,
): NavItemContribution[] {
  const items: NavItemContribution[] = []
  for (const c of contributions) {
    const sectionItems = c.nav?.[section] ?? []
    for (const item of sectionItems) {
      items.push(item)
    }
  }

  // Decorate with original index so the sort stays stable across runtimes
  // (Array.prototype.sort is spec-stable in modern engines, but tying the
  // comparison to an explicit index makes the contract obvious).
  const decorated = items.map((item, index) => ({ item, index }))
  decorated.sort((a, b) => {
    const aHas = a.item.order !== undefined
    const bHas = b.item.order !== undefined
    if (aHas && bHas) {
      const orderDelta = (a.item.order as number) - (b.item.order as number)
      if (orderDelta !== 0) return orderDelta
      return a.index - b.index
    }
    if (aHas) return -1
    if (bHas) return 1
    return a.index - b.index
  })
  return decorated.map((d) => d.item)
}

// ---------------------------------------------------------------------------
// Badge provider extraction
// ---------------------------------------------------------------------------

function providersFor(items: NavItemContribution[], section: Section): BadgeProvider[] {
  const providers: BadgeProvider[] = []
  for (const item of items) {
    if (item.useBadge !== undefined) {
      providers.push({
        itemId: item.id,
        section,
        useBadge: item.useBadge,
      })
    }
  }
  return providers
}

// ---------------------------------------------------------------------------
// Mapping to UI NavItem
// ---------------------------------------------------------------------------

function toNavItem(
  contribution: NavItemContribution,
  section: Section,
  badges: BadgeContext,
): NavItem {
  const key = `${section}:${contribution.id}`
  const badge = badges.values.get(key)
  return {
    id: contribution.id,
    label: contribution.label,
    icon: contribution.icon,
    href: contribution.href,
    badge,
  }
}
